// UploadEvidenceDialog — modal form for uploading evidence files.
// Accepts a file, evidence tag selection, and optional comment.
// Uses multipart/form-data upload via the provided uploadFn.

import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { evidenceTagsApi } from '@/lib/api/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Upload, FileUp, X } from 'lucide-react'
import type { EvidenceItem, UploadEvidenceParams } from './EvidenceTab'

// --- Helpers ---

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50 MB

// --- Props ---

interface UploadEvidenceDialogProps {
  open: boolean
  onClose: () => void
  uploadFn: (params: UploadEvidenceParams) => Promise<EvidenceItem>
  queryKey: readonly unknown[]
}

// --- Component ---

export function UploadEvidenceDialog({
  open,
  onClose,
  uploadFn,
  queryKey,
}: UploadEvidenceDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [tagId, setTagId] = useState('')
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Fetch evidence tags for the selector
  const tagsApi = evidenceTagsApi(api)
  const { data: tags, isLoading: tagsLoading } = useQuery({
    queryKey: queryKeys.frameworkConfig.evidenceTags(slug),
    queryFn: () => tagsApi.list(),
    enabled: open,
  })

  const uploadMutation = useMutation({
    mutationFn: (params: UploadEvidenceParams) => uploadFn(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      resetAndClose()
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  function resetAndClose() {
    setSelectedFile(null)
    setTagId('')
    setComment('')
    setError(null)
    onClose()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    if (file && file.size > MAX_FILE_SIZE) {
      setError(`File size (${formatFileSize(file.size)}) exceeds the 50 MB limit.`)
      setSelectedFile(null)
      return
    }
    setError(null)
    setSelectedFile(file)
  }

  function handleRemoveFile() {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  function handleSubmit() {
    if (!selectedFile) {
      setError('Please select a file to upload.')
      return
    }
    if (!tagId) {
      setError('Please select an evidence tag.')
      return
    }
    setError(null)
    uploadMutation.mutate({
      file: selectedFile,
      evidenceTagId: tagId,
      comment: comment.trim() || undefined,
    })
  }

  const canSubmit = selectedFile !== null && tagId !== '' && !uploadMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Attach Evidence</DialogTitle>
          <DialogDescription>
            Upload a file and tag it for classification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File selection */}
          <div className="space-y-2">
            <Label htmlFor="evidence-file">File</Label>
            {selectedFile ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                <FileUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm">{selectedFile.name}</span>
                <span className="text-xs text-muted-foreground">
                  {formatFileSize(selectedFile.size)}
                </span>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="ml-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  id="evidence-file"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-muted-foreground"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Choose file...
                </Button>
                <p className="mt-1 text-xs text-muted-foreground">
                  Maximum file size: 50 MB
                </p>
              </div>
            )}
          </div>

          {/* Evidence tag selector */}
          <div className="space-y-2">
            <Label htmlFor="evidence-tag">Evidence Tag</Label>
            <Select value={tagId} onValueChange={setTagId}>
              <SelectTrigger>
                <SelectValue placeholder={tagsLoading ? 'Loading tags...' : 'Select a tag'} />
              </SelectTrigger>
              <SelectContent>
                {(tags ?? []).map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    {tag.tag_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!tagsLoading && (tags ?? []).length === 0 && (
              <p className="text-xs text-amber-600">
                No evidence tags configured. Ask your admin to create evidence tags in Framework
                Settings.
              </p>
            )}
          </div>

          {/* Optional comment */}
          <div className="space-y-2">
            <Label htmlFor="evidence-comment">Comment (optional)</Label>
            <Input
              id="evidence-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Brief description of this evidence..."
              maxLength={500}
            />
          </div>

          {/* Error message */}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={resetAndClose} disabled={uploadMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
